import { useEffect, useLayoutEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";
import { CourseBreadcrumb } from "@/components/course/CourseBreadcrumb";
import { GlossaryHighlightProvider } from "@/components/glossary/GlossaryHighlightContext";
import { isUnveilQuery } from "@/lib/chapterNavigation";
import { stripThirdLevelNumbering } from "@/lib/courseUtils";
import PageHeadline from "@/components/course/PageHeadline";
import { SubchapterNavigation } from "@/components/course/SubchapterNavigation";
import StoryboardList from "@/components/storyboard/StoryboardList";
import { CourseIntroPage } from "@/components/course/CourseIntroPage";
import { ModuleIntroPage } from "@/components/course/ModuleIntroPage";
import ChapterIntroPage from "@/components/course/ChapterIntroPage";
import { ModuleOutroPage } from "@/components/course/ModuleOutroPage";
import { ChapterOutroPage } from "@/components/course/ChapterOutroPage";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { contentEnterClass } from "@/lib/contentEnterAnimation";
import { cn } from "@/lib/utils";

const contentSectionClass =
  "flex-1 bg-background overflow-y-auto px-10 pt-4 pb-10";
const introSectionClass = "flex-1 bg-bg-warm overflow-y-auto px-10 pt-8";
const outroSectionClass = "flex-1 bg-background overflow-y-auto px-10 pt-8";
const chapterIntroSectionClass = "min-h-0 flex-1 overflow-y-auto bg-background";

const lessonTitle = (node: CourseTreeNode) => {
  if (node.type === CourseTreeNodeTypeEnum.Subchapter) {
    return stripThirdLevelNumbering(node.title);
  }
  return node.title;
};

export const CourseContent = () => {
  const {
    selectedNodeContent,
    selectedContentElements,
    selectedNodeId,
    storyboards,
    storyboardsLoading,
    viewMode,
  } = useCourseDetailStore();
  const courseId = useCourseDetailStore((state) => state.courseStructureId);
  const glossaryTerms = useCourseGlossaryStore((state) => state.terms);
  const fetchGlossary = useCourseGlossaryStore((state) => state.fetchGlossary);
  const sectionRef = useRef<HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const isChapterOutroView =
    viewMode === "outro" &&
    selectedNodeContent?.type === CourseTreeNodeTypeEnum.Chapter;
  // Unveil deep-links and chapter-outro stacks manage their own scroll;
  // resetting to top would race those transitions.
  const shouldScrollContentToTop =
    !isUnveilQuery(searchParams) && !isChapterOutroView;

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!shouldScrollContentToTop || !section) return;
    // Instant: leftover Abschluss scroll must not paint as the next chapter's
    // unveiled first lesson, then ease up to Einführung.
    section.scrollTop = 0;
  }, [selectedNodeId, viewMode, shouldScrollContentToTop]);

  // Load glossary terms up front so course content can be auto-linked without
  // waiting for the user to open the slideover.
  useEffect(() => {
    if (courseId) fetchGlossary(courseId);
  }, [courseId, fetchGlossary]);

  const isSubchapterContent =
    viewMode === "content" &&
    selectedNodeContent?.type === CourseTreeNodeTypeEnum.Subchapter;
  const isChapterSection =
    selectedNodeContent?.type === CourseTreeNodeTypeEnum.Chapter &&
    (viewMode === "intro" || viewMode === "outro");
  const isModuleSection =
    selectedNodeContent?.type === CourseTreeNodeTypeEnum.Module &&
    (viewMode === "intro" || viewMode === "outro");

  // Intro/Outro pages
  if (viewMode === "intro" && !selectedNodeContent) {
    return (
      <section ref={sectionRef} className={introSectionClass}>
        <CourseIntroPage />
      </section>
    );
  }

  if (isModuleSection) {
    const sectionClass =
      viewMode === "intro" ? introSectionClass : outroSectionClass;

    return (
      <section ref={sectionRef} className={sectionClass}>
        {viewMode === "intro" ? (
          <ModuleIntroPage module={selectedNodeContent} />
        ) : (
          <ModuleOutroPage module={selectedNodeContent} />
        )}
      </section>
    );
  }

  if (isChapterSection) {
    // key resets the pane on each chapter + view so scroll starts at 0,
    // preventing leftover Abschluss scrollTop from triggering a false unveil.
    return (
      <section
        key={`${selectedNodeId}-${viewMode}`}
        ref={sectionRef}
        className={chapterIntroSectionClass}
      >
        {viewMode === "intro" ? (
          <ChapterIntroPage chapter={selectedNodeContent} />
        ) : (
          <ChapterOutroPage chapter={selectedNodeContent} />
        )}
      </section>
    );
  }

  if (!selectedNodeContent) {
    return (
      <section ref={sectionRef} className={contentSectionClass}>
        <div className="max-w-4xl">
          <p className="text-muted-foreground">Select a node to view content</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className={contentSectionClass}>
      <div key={selectedNodeId} className={cn("max-w-full", contentEnterClass)}>
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <CourseBreadcrumb />
          </div>
        </div>

        <PageHeadline
          title={lessonTitle(selectedNodeContent)}
          level="h1"
          className="mb-8"
        />

        <GlossaryHighlightProvider
          terms={isSubchapterContent ? glossaryTerms : []}
          resetKey={selectedNodeId}
        >
          <StoryboardList
            loading={storyboardsLoading}
            storyboards={storyboards}
            selectedContentElements={selectedContentElements}
          />
        </GlossaryHighlightProvider>

        {selectedNodeContent.type === CourseTreeNodeTypeEnum.Subchapter && (
          <SubchapterNavigation />
        )}
      </div>
    </section>
  );
};
